import { useCallback, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';

import {
  INSURANCE_CARD_PREVIEW_INTERACTIVE_SX,
  INSURANCE_CARD_PREVIEW_SX,
} from '../constants';
import { DocumentPreviewDialog, isPdfDocumentFileType } from '../../../../../../common';

import type { KeyboardEvent, ReactNode } from 'react';
import type { InsuranceCardPreviewProps } from './types';

export default function InsuranceCardPreview({
  documentFileType = '',
  documentPreviewUrl = '',
  illustrationAlt,
  illustrationSrc,
  previewLoading = false,
}: InsuranceCardPreviewProps) {
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);

  const handleClosePreviewDialog = useCallback(() => setPreviewDialogOpen(false), []);

  const handleOpenPreviewDialog = useCallback(() => setPreviewDialogOpen(true), []);

  const handlePreviewKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleOpenPreviewDialog();
      }
    },
    [handleOpenPreviewDialog],
  );

  const isPreviewClickable = Boolean(documentPreviewUrl) && !previewLoading;

  const wrapPreview = (preview: ReactNode) => {
    if (!isPreviewClickable) {
      return preview;
    }

    return (
      <Box
        aria-label={`View enlarged ${illustrationAlt}`}
        onClick={handleOpenPreviewDialog}
        onKeyDown={handlePreviewKeyDown}
        role="button"
        sx={INSURANCE_CARD_PREVIEW_INTERACTIVE_SX}
        tabIndex={0}
      >
        {preview}
      </Box>
    );
  };

  if (previewLoading) {
    return (
      <Box
        sx={{
          ...INSURANCE_CARD_PREVIEW_SX,
          alignItems: 'center',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (!documentPreviewUrl) {
    return (
      <Box
        component="img"
        alt={illustrationAlt}
        src={illustrationSrc}
        sx={INSURANCE_CARD_PREVIEW_SX}
      />
    );
  }

  if (isPdfDocumentFileType(documentFileType)) {
    return (
      <>
        {wrapPreview(
          <Box
            component="iframe"
            referrerPolicy="no-referrer"
            sandbox=""
            src={documentPreviewUrl}
            title={illustrationAlt}
            sx={[INSURANCE_CARD_PREVIEW_SX, { border: 0, pointerEvents: 'none' }]}
          />,
        )}
        <DocumentPreviewDialog
          documentFileType={documentFileType}
          documentPreviewUrl={documentPreviewUrl}
          illustrationAlt={illustrationAlt}
          onClose={handleClosePreviewDialog}
          open={previewDialogOpen}
        />
      </>
    );
  }

  return (
    <>
      {wrapPreview(
        <Box
          component="img"
          alt={illustrationAlt}
          src={documentPreviewUrl}
          sx={[INSURANCE_CARD_PREVIEW_SX, { objectFit: 'contain' }]}
        />,
      )}
      <DocumentPreviewDialog
        documentFileType={documentFileType}
        documentPreviewUrl={documentPreviewUrl}
        illustrationAlt={illustrationAlt}
        onClose={handleClosePreviewDialog}
        open={previewDialogOpen}
      />
    </>
  );
}
